import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTheme } from "../context/ThemeContext";

export default function DevelopersPage() {
  const [loaded, setLoaded] = useState(false);
  const { darkMode } = useTheme();

  useEffect(() => {
    setLoaded(true);
    return () => setLoaded(false); // Cleanup function
  }, []);

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const floatingVariants = {
    float: {
      y: [0, -10, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  // Theme-based colors (using darkMode consistently)
  const bgGradient = darkMode ? "from-gray-800 to-gray-900" : "from-gray-50 to-gray-100";
  const cardBg = darkMode ? "bg-gray-800 bg-opacity-90" : "bg-white bg-opacity-80";
  const textColor = darkMode ? "text-gray-200" : "text-gray-800";
  const textSecondary = darkMode ? "text-gray-400" : "text-gray-600";
  const accentColor = darkMode ? "text-indigo-400" : "text-indigo-600";
  const borderColor = darkMode ? "border-gray-700" : "border-gray-200";
  const buttonBg = darkMode
    ? "bg-indigo-700 hover:bg-indigo-600"
    : "bg-indigo-600 hover:bg-indigo-700";
  const floatingBlue = darkMode ? "bg-blue-900 opacity-20" : "bg-blue-100 opacity-40";
  const floatingIndigo = darkMode ? "bg-indigo-900 opacity-20" : "bg-indigo-100 opacity-40";
  const floatingPurple = darkMode ? "bg-purple-900 opacity-20" : "bg-purple-100 opacity-40";
  const bulletColor = darkMode ? "bg-indigo-400" : "bg-indigo-500";

  return (
    <div
      className={`min-h-screen bg-gradient-to-br ${bgGradient} flex flex-col items-center px-4 py-8 sm:px-6 lg:px-8`}
    >
      {/* Floating background elements */}
      <motion.div
        className={`absolute top-1/4 left-1/4 w-24 h-24 sm:w-32 sm:h-32 rounded-full ${floatingBlue}`}
        variants={floatingVariants}
        animate="float"
      />
      <motion.div
        className={`absolute top-1/3 right-1/4 w-20 h-20 sm:w-24 sm:h-24 rounded-full ${floatingIndigo}`}
        variants={floatingVariants}
        animate="float"
        style={{ y: [0, -15, 0] }}
      />
      <motion.div
        className={`absolute bottom-1/4 right-1/3 w-22 h-22 sm:w-28 sm:h-28 rounded-full ${floatingPurple}`}
        variants={floatingVariants}
        animate="float"
        style={{ y: [0, -20, 0] }}
      />

      <motion.div
        className={`max-w-4xl w-full ${cardBg} p-6 sm:p-8 md:p-10 rounded-xl shadow-sm backdrop-blur-sm relative z-10 ${borderColor} border`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <motion.div
          className="absolute inset-0 rounded-xl pointer-events-none"
          animate={{
            borderColor: darkMode
              ? ["#374151", "#818cf8", "#374151"]
              : ["#e5e7eb", "#6366f1", "#e5e7eb"],
            boxShadow: darkMode
              ? [
                  "0 0 0 rgba(129, 140, 248, 0)",
                  "0 0 10px rgba(129, 140, 248, 0.3)",
                  "0 0 0 rgba(129, 140, 248, 0)",
                ]
              : [
                  "0 0 0 rgba(99, 102, 241, 0)",
                  "0 0 10px rgba(99, 102, 241, 0.2)",
                  "0 0 0 rgba(99, 102, 241, 0)",
                ],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse",
          }}
          style={{ border: "1px solid" }}
        />

        <motion.h1
          className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-6 text-center ${textColor}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          Meet the Developer
        </motion.h1>

        <motion.section
          className="space-y-6 sm:space-y-8"
          variants={container}
          initial="hidden"
          animate={loaded ? "show" : "hidden"}
        >
          <motion.div variants={item}>
            <h2
              className={`text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 ${textColor} flex items-center`}
            >
              <span className={`w-2 h-2 ${bulletColor} rounded-full mr-3`}></span>
              About the Developer
            </h2>
            <p className={`${textSecondary} leading-relaxed`}>
              The developer of <span className={`font-medium ${accentColor}`}>printkaro.shop</span>{" "}
              is <span className={`font-medium ${textColor}`}>Sahil Soni</span>, a skilled
              full-stack developer specializing in both frontend and backend technologies. Sahil
              holds a degree in Computer Science and Engineering from Manav Rachna International
              Institute of Research and Studies, with a focus on Artificial Intelligence and Machine
              Learning.
            </p>
          </motion.div>

          <motion.div variants={item}>
            <p className={`${textSecondary} leading-relaxed`}>
              Sahil's technical expertise spans across using Firebase for backend services,
              including Firebase Functions for serverless computing and Firebase Firestore for
              real-time NoSQL database management. His frontend work relies on technologies such as
              React.js and Tailwind CSS to build fast, responsive applications.
            </p>
          </motion.div>

          <motion.div variants={item}>
            <h2
              className={`text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 ${textColor} flex items-center`}
            >
              <span className={`w-2 h-2 ${bulletColor} rounded-full mr-3`}></span>
              Key Features of the Website
            </h2>
            <ul className="space-y-2 sm:space-y-3">
              <li className="flex items-start">
                <svg
                  className={`w-5 h-5 ${accentColor} mr-2 mt-0.5 flex-shrink-0`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className={textSecondary}>
                  User-friendly interface for creating virtual phone numbers
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className={`w-5 h-5 ${accentColor} mr-2 mt-0.5 flex-shrink-0`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className={textSecondary}>
                  Instant OTP generation and verification system using Firebase functions
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className={`w-5 h-5 ${accentColor} mr-2 mt-0.5 flex-shrink-0`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className={textSecondary}>
                  Real-time communication features powered by Firebase and WebSockets
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className={`w-5 h-5 ${accentColor} mr-2 mt-0.5 flex-shrink-0`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className={textSecondary}>
                  Scalable backend leveraging Firebase's cloud infrastructure for seamless
                  performance
                </span>
              </li>
            </ul>
          </motion.div>

          <motion.div variants={item}>
            <h2
              className={`text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 ${textColor} flex items-center`}
            >
              <span className={`w-2 h-2 ${bulletColor} rounded-full mr-3`}></span>
              Contact the Developer
            </h2>
            <p className={`${textSecondary} leading-relaxed`}>
              If you need any support or wish to get in touch with the developer, feel free to reach
              out via email at{" "}
              <motion.span
                className={`font-medium ${accentColor}`}
                whileHover={{
                  scale: 1.02,
                  transition: { duration: 0.2 },
                }}
              >
                ssoni0185@gmail.com
              </motion.span>
              .
            </p>
          </motion.div>

          <motion.div variants={item}>
            <h2
              className={`text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 ${textColor} flex items-center`}
            >
              <span className={`w-2 h-2 ${bulletColor} rounded-full mr-3`}></span>
              Developer's Experience
            </h2>
            <p className={`${textSecondary} leading-relaxed`}>
              Sahil has previously worked as a backend developer at RHS Communications, where he
              built scalable real-time systems using WebSockets and integrated RESTful APIs. His
              passion for building efficient and reliable systems continues to drive his work in
              full-stack development, particularly focusing on Firebase's cloud tools to build
              serverless applications.
            </p>
          </motion.div>
        </motion.section>

        <motion.div
          className="mt-8 sm:mt-10 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <Link
            to="/"
            className={`inline-flex items-center px-5 py-2 sm:px-6 sm:py-3 border border-transparent text-sm sm:text-base font-medium rounded-md text-white ${buttonBg} transition-colors duration-200`}
          >
            Back to Home
            <svg
              className="ml-2 -mr-1 w-4 h-4 sm:w-5 sm:h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
}
